import './style.scss';
import React from 'react';
import { LineHelper } from '../../../helpers/line.helper';
import { ILine } from '../../../models/line.model';
import { t } from '../../../services/texts.service';

/**
 * LineBadge
 * Renders a compact, color-branded badge for a public transport line.
 *
 * Behavior
 * - Colors (background, foreground, border) are derived from the provided line via LineHelper.
 * - Accessible: includes a visually hidden "Line" label for screen readers.
 * - Displayed text is computed by LineBadgeHelper.getLineName:
 *   - Train lines: "<name> <designation>" or just "<name>" if designation is missing.
 *   - Other modes (e.g., tram, bus, ferry, taxi): "<shortName>".
 *
 * Props
 * - line: The line to render.
 * - useShortName?: If true, always use the short line name. Default is false.
 * - useShortNameAndNumberForTrain?: If true, use the train's short name and designation. Default is false.
 * - className?: Optional extra CSS classes to append to the outer span.
 *
 * Remarks
 * - This component replaces the LineIcon component.
 *
 * Example
 * <LineBadge line={line} className="me-2" />
 */
export default function LineBadge({
  line,
  useShortName = false,
  className = '',
  useShortNameAndNumberForTrain = false,
}: {
  line: ILine;
  useShortName?: boolean;
  className?: string;
  useShortNameAndNumberForTrain?: boolean;
}): React.ReactNode {
  return (
    <span
      className={`line-badge px-1 flex-shrink-0 rounded d-flex align-items-center justify-content-center ${className ?? ''}`}
      style={{
        minHeight: '1.5rem',
        minWidth: '1.5rem',
        lineHeight: '1rem',
        backgroundColor: LineHelper.getBackgroundColor(line),
        color: LineHelper.getForegroundColor(line),
        borderColor: LineHelper.getBorderColor(line),
      }}
    >
      <span className="visually-hidden">{t.line}</span>
      <span className="fw-bold small flex-shrink-0 text-nowrap">{LineHelper.getLineName(line, useShortName, useShortNameAndNumberForTrain)}</span>
    </span>
  );
}
